// Types
export interface DialogLine {
    id: string;
    text: string;
    voiceId: string;
}

export interface TextSegment {
    id: string;
    startIndex: number;
    endIndex: number;
    voiceId: string;
    text: string;
}

export interface Capsule {
    id: string;
    textValue: string;
    textObject: string;
    selectedContentLanguage: string;
    selectedVoice: string;
    selectedSecondVoice: string;
    selectedTextType: "isText" | "isdialog";
    audioSpeed: number;
    audioModel: string;
    dialogLines: DialogLine[];
    useMultipleNarrators: boolean;
    textSegments: TextSegment[];
    audioId?: string;
    audioUrl?: string | null;
    audioName?: string; // Original name from database variant (for reference, NOT used in audio player)
}

// Constants
export const DIALOG_LINE_SEPARATOR = "\n\n";

// Utility Functions
export const normalizeDialogLines = (
    dialogLinesRaw: any[] | undefined,
): { dialogLines: DialogLine[]; segments: TextSegment[]; fullText: string; voiceIds: string[] } => {
    if (!dialogLinesRaw || dialogLinesRaw.length === 0) {
        return { dialogLines: [], segments: [], fullText: "", voiceIds: [] };
    }

    const normalizedDialogLines: DialogLine[] = [];
    const segments: TextSegment[] = [];
    const voiceIdsSet = new Set<string>();
    const baseTimestamp = Date.now();
    let fullText = "";

    dialogLinesRaw.forEach((rawLine, index) => {
        const text = typeof rawLine?.text === "string" ? rawLine.text : "";
        // Check for voice clone external ID
        const cloneExternalId =
            typeof rawLine?.voiceClone?.externalVoiceId === "string" && rawLine.voiceClone.externalVoiceId
                ? rawLine.voiceClone.externalVoiceId
                : "";
        // Check for prompt voice external ID
        const promptExternalId =
            typeof rawLine?.voicePrompt?.externalVoiceId === "string" && rawLine.voicePrompt.externalVoiceId
                ? rawLine.voicePrompt.externalVoiceId
                : "";
        // Priority: cloneExternalId > promptExternalId > voiceAIId > voiceId
        const voiceId =
            cloneExternalId ||
            promptExternalId ||
            (typeof rawLine?.voiceAIId === "string" && rawLine.voiceAIId
                ? rawLine.voiceAIId
                : typeof rawLine?.voiceId === "string"
                  ? rawLine.voiceId
                  : "");
        const lineId =
            typeof rawLine?.id === "string" && rawLine.id.trim() !== ""
                ? rawLine.id
                : `dialog-${baseTimestamp}-${index + 1}`;
        const startIndex = fullText.length;

        fullText += text;
        const endIndex = fullText.length;

        normalizedDialogLines.push({
            id: lineId,
            text,
            voiceId: voiceId || "",
        });

        segments.push({
            id: `segment-${lineId}`,
            startIndex,
            endIndex,
            voiceId: voiceId || "",
            text,
        });

        if (voiceId) {
            voiceIdsSet.add(voiceId);
        }

        // Don't add line breaks between dialog lines - preserve normal text flow
        // Only add a space if needed for proper text flow
        if (index < dialogLinesRaw.length - 1) {
            const nextLine = dialogLinesRaw[index + 1];
            const nextText = typeof nextLine?.text === "string" ? nextLine.text.trim() : "";
            // Add space only if next line doesn't start with punctuation and current doesn't end with punctuation
            const currentEndsWithPunct = /[.!?,:;]$/.test(text.trim());
            const nextStartsWithPunct = /^[.!?,:;]/.test(nextText);
            if (!currentEndsWithPunct && !nextStartsWithPunct && text.trim() && nextText) {
                fullText += " ";
            }
        }
    });

    return {
        dialogLines: normalizedDialogLines,
        segments,
        fullText,
        voiceIds: Array.from(voiceIdsSet),
    };
};

export const parseLegacyCapsules = (
    rawText: string | null | undefined,
    mainTextObject: string,
): { number: number; title: string; content: string }[] => {
    if (!rawText || typeof rawText !== "string") {
        return [];
    }

    const markerRegex = /Capsule\s+(\d+)\s*-\s*/gi;
    const markers: { index: number; length: number; number: number }[] = [];

    let match: RegExpExecArray | null;
    while ((match = markerRegex.exec(rawText)) !== null) {
        markers.push({
            index: match.index,
            length: match[0].length,
            number: parseInt(match[1], 10),
        });
    }

    if (markers.length === 0) {
        return [];
    }

    const capsules = markers.map((marker, idx) => {
        const start = marker.index;
        const end = idx + 1 < markers.length ? markers[idx + 1].index : rawText.length;
        const segment = rawText.slice(start, end).trim();

        const newlineIndex = segment.indexOf("\n");
        const header = newlineIndex === -1 ? segment : segment.slice(0, newlineIndex).trim();
        const body = newlineIndex === -1 ? "" : segment.slice(newlineIndex + 1).trim();

        // Extract title after "Capsule X -" until newline
        let title = header.replace(/^Capsule\s+\d+\s*-\s*/i, "").trim();
        let content = body;

        if (!content && title.includes(":")) {
            const [titlePart, ...contentParts] = title.split(":");
            title = titlePart.trim();
            content = contentParts.join(":").trim();
        }

        title = title.replace(/[:：]\s*$/, "");

        // Build full title: "mainTextObject - Capsule X - [extracted title]"
        const capsuleLabel = `Capsule ${marker.number}`;
        const fullTitle = title
            ? `${mainTextObject} - ${capsuleLabel} - ${title}`
            : `${mainTextObject} - ${capsuleLabel}`;

        return {
            number: Number.isFinite(marker.number) ? marker.number : idx + 1,
            title: fullTitle,
            content,
        };
    });

    return capsules.filter((capsule) => capsule.title || capsule.content);
};

export const collectVoiceIds = ({
    primaryVoiceId,
    dialogLines,
    segments,
    capsules,
    additionalVoiceIds,
}: {
    primaryVoiceId?: string | null;
    dialogLines?: DialogLine[];
    segments?: TextSegment[];
    capsules?: Capsule[];
    additionalVoiceIds?: (string | null | undefined)[];
}): string[] => {
    const voiceIds = new Set<string>();

    const addVoice = (voiceId?: string | null) => {
        if (voiceId && typeof voiceId === "string" && voiceId.trim() !== "") {
            voiceIds.add(voiceId);
        }
    };

    addVoice(primaryVoiceId);
    dialogLines?.forEach((line) => addVoice(line.voiceId));
    segments?.forEach((segment) => addVoice(segment.voiceId));
    capsules?.forEach((capsule) => {
        addVoice(capsule.selectedVoice);
        addVoice(capsule.selectedSecondVoice);
        capsule.dialogLines?.forEach((line) => addVoice(line.voiceId));
        capsule.textSegments?.forEach((segment) => addVoice(segment.voiceId));
    });
    additionalVoiceIds?.forEach((voiceId) => addVoice(voiceId));

    return Array.from(voiceIds);
};

export const convertSegmentsToDialogLines = (
    text: string,
    segments: TextSegment[],
    defaultVoiceId: string,
): DialogLine[] => {
    if (segments.length === 0 || !text || text.trim() === "") {
        return [];
    }

    // Sort segments by start index and filter out invalid segments
    const sortedSegments = [...segments]
        .filter((seg) => {
            const isValid = seg.startIndex >= 0 && seg.endIndex <= text.length && seg.startIndex < seg.endIndex;
            if (!isValid) {
                console.warn("[convertSegmentsToDialogLines] Invalid segment filtered out:", {
                    id: seg.id,
                    startIndex: seg.startIndex,
                    endIndex: seg.endIndex,
                    textLength: text.length,
                    segmentText: seg.text,
                });
            }
            return isValid;
        })
        .sort((a, b) => a.startIndex - b.startIndex);

    console.log("[convertSegmentsToDialogLines] Valid sorted segments:", sortedSegments.length);
    sortedSegments.forEach((seg, idx) => {
        const extractedText = text.substring(seg.startIndex, seg.endIndex);
        const matchesStored = seg.text === extractedText;
        console.log(`  Segment ${idx}:`, {
            id: seg.id,
            startIndex: seg.startIndex,
            endIndex: seg.endIndex,
            voiceId: seg.voiceId,
            extractedText: extractedText,
            storedText: seg.text,
            matches: matchesStored,
            warning: !matchesStored ? "⚠️ Text mismatch - indices may be stale!" : undefined,
        });
    });

    if (sortedSegments.length === 0) {
        console.log("[convertSegmentsToDialogLines] ⚠️ Early return: no valid sorted segments");
        return [];
    }

    // Validate segment coverage and detect gaps/overlaps
    let lastEnd = 0;
    const gaps: Array<{ start: number; end: number }> = [];
    sortedSegments.forEach((seg, idx) => {
        if (seg.startIndex > lastEnd) {
            gaps.push({ start: lastEnd, end: seg.startIndex });
            console.warn(`[convertSegmentsToDialogLines] Gap detected before segment ${idx}:`, {
                gapStart: lastEnd,
                gapEnd: seg.startIndex,
                gapText: text.substring(lastEnd, seg.startIndex),
            });
        }
        if (seg.startIndex < lastEnd) {
            console.warn(`[convertSegmentsToDialogLines] Overlap detected at segment ${idx}:`, {
                previousEnd: lastEnd,
                currentStart: seg.startIndex,
            });
        }
        lastEnd = Math.max(lastEnd, seg.endIndex);
    });

    // Create dialog lines from segments
    const dialogLines: DialogLine[] = [];
    let currentIndex = 0;

    sortedSegments.forEach((segment, idx) => {
        // Ensure indices are within text bounds
        const safeStart = Math.max(0, Math.min(segment.startIndex, text.length));
        const safeEnd = Math.max(safeStart, Math.min(segment.endIndex, text.length));

        // Add text before segment as a line with default voice (if any)
        if (safeStart > currentIndex) {
            const beforeText = text.substring(currentIndex, safeStart).trim();
            if (beforeText) {
                if (defaultVoiceId && defaultVoiceId.trim() !== "") {
                    const beforeLine = {
                        id: `dialog-${Date.now()}-${Math.random()}`,
                        text: beforeText,
                        voiceId: defaultVoiceId,
                    };
                    dialogLines.push(beforeLine);
                    console.log(`[convertSegmentsToDialogLines] ➕ Added before-segment line ${idx}:`, beforeLine);
                } else {
                    console.warn(
                        `[convertSegmentsToDialogLines] ⚠️ Skipped before-segment text (no default voice):`,
                        beforeText,
                    );
                }
            }
        }

        // Add segment as dialog line
        // CRITICAL: Use segment.text if available and indices seem wrong, otherwise extract from text
        let segmentText: string;
        const extractedFromIndices = text.substring(safeStart, safeEnd).trim();

        // If segment has stored text, validate it matches extracted text
        if (segment.text && segment.text.trim() !== "") {
            const storedText = segment.text.trim();
            if (extractedFromIndices === storedText) {
                // Indices are correct, use extracted text
                segmentText = extractedFromIndices;
            } else {
                // Indices are stale! Use stored text as fallback
                console.warn(
                    `[convertSegmentsToDialogLines] ⚠️ Index mismatch for segment ${idx}, using stored text:`,
                    {
                        extracted: extractedFromIndices,
                        stored: storedText,
                        startIndex: safeStart,
                        endIndex: safeEnd,
                    },
                );
                segmentText = storedText;
            }
        } else {
            // No stored text, use extracted text
            segmentText = extractedFromIndices;
        }

        if (segmentText && segment.voiceId && segment.voiceId.trim() !== "") {
            const segmentLine = {
                id: segment.id,
                text: segmentText,
                voiceId: segment.voiceId,
            };
            dialogLines.push(segmentLine);
            console.log(`[convertSegmentsToDialogLines] ✅ Added segment line ${idx}:`, segmentLine);
        } else {
            console.warn(`[convertSegmentsToDialogLines] ⚠️ Skipped segment ${idx}:`, {
                segmentText: segmentText,
                hasVoiceId: !!segment.voiceId,
                voiceId: segment.voiceId,
            });
        }

        currentIndex = safeEnd;
    });

    // Add remaining text after last segment
    if (currentIndex < text.length) {
        const remainingText = text.substring(currentIndex).trim();
        if (remainingText) {
            if (defaultVoiceId && defaultVoiceId.trim() !== "") {
                const remainingLine = {
                    id: `dialog-${Date.now()}-${Math.random()}`,
                    text: remainingText,
                    voiceId: defaultVoiceId,
                };
                dialogLines.push(remainingLine);
                console.log("[convertSegmentsToDialogLines] ➕ Added remaining text line:", remainingLine);
            } else {
                console.warn(
                    "[convertSegmentsToDialogLines] ⚠️ Skipped remaining text (no default voice):",
                    remainingText,
                );
            }
        }
    }

    // Filter out empty dialog lines, and fold punctuation-only lines (e.g. a trailing "...") into the previous line:
    // sent alone, ElevenLabs improvises speech for them
    const filtered: DialogLine[] = [];
    dialogLines.forEach((line) => {
        if (!line.text || line.text.trim() === "" || !line.voiceId) return;
        if (/\p{L}|\p{N}/u.test(line.text)) {
            filtered.push(line);
        } else if (filtered.length > 0) {
            const previous = filtered[filtered.length - 1];
            filtered[filtered.length - 1] = { ...previous, text: `${previous.text.trimEnd()} ${line.text.trim()}` };
        }
    });

    console.log("[convertSegmentsToDialogLines] Final result:", {
        totalDialogLines: filtered.length,
        dialogLines: filtered.map((line, idx) => ({
            idx,
            voiceId: line.voiceId,
            textLength: line.text.length,
            textPreview: line.text.substring(0, 50) + (line.text.length > 50 ? "..." : ""),
        })),
    });

    return filtered;
};

export const calculateWordCount = (capsules: Capsule[]): number => {
    const allText = capsules.map((cap) => cap.textValue || "").join(" ");
    return allText.trim() ? allText.trim().split(/\s+/).length : 0;
};

export const calculateCapsuleCharacters = (capsule: Capsule): number => {
    if (capsule.dialogLines && capsule.dialogLines.length > 0) {
        return capsule.dialogLines.reduce((lineSum, line) => {
            return lineSum + (line?.text ? line.text.length : 0);
        }, 0);
    }
    if (capsule.textSegments && capsule.textSegments.length > 0) {
        return capsule.textSegments.reduce((segSum, seg) => {
            return segSum + (seg?.text ? seg.text.length : 0);
        }, 0);
    }
    return capsule.textValue ? capsule.textValue.length : 0;
};

export const calculateTotalCharacters = (capsules: Capsule[]): number => {
    return capsules.reduce((sum, capsule) => sum + calculateCapsuleCharacters(capsule), 0);
};
